// react
import { useCallback, useEffect, useState } from 'react';

// types
import { ExceptionPayload, Exception } from '../@types';

// services
import {
	createException,
	fetchException,
	fetchExceptions,
	ApiResponse,
	QueryParams,
} from '../services/api';

// Types

export interface UseExceptionsParams {
	isEnabled?: boolean;
	projectId?: number;
	query?: QueryParams;
}

export interface UseExceptionsReturn {
	create: (payload: ExceptionPayload) => Promise<ApiResponse<Exception>>;
	data: Exception[];
	error: string | null;
	loading: boolean;
	pagination: ApiResponse<Exception[]>['pagination'];
	refetch: () => Promise<void>;
}

// Hook

export const useExceptions = ({
	isEnabled = true,
	projectId,
	query = {},
}: UseExceptionsParams = {}): UseExceptionsReturn => {
	// auth
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// state
	const [data, setData] = useState<Exception[]>([]);
	const [error, setError] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);
	const [pagination, setPagination] = useState<ApiResponse<Exception[]>['pagination']>(undefined);

	// fetch data
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const queryKey = JSON.stringify(query);

	const fetchData = useCallback(async () => {
		if (!accessToken || !idToken) {
			return;
		}

		setLoading(true);
		setError(null);

		try {
			const res = await fetchExceptions({ accessToken, idToken }, { ...query, project: projectId });

			if (res.error) {
				setError(res.error.message);
			} else {
				setData(res.data || []);
				setPagination(res.pagination);
			}
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Failed to fetch exceptions');
		} finally {
			setLoading(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [accessToken, idToken, projectId, queryKey]);

	// initial fetch
	useEffect(() => {
		if (isEnabled && projectId) {
			fetchData();
		}
	}, [isEnabled, fetchData, projectId]);

	// create exception
	const create = useCallback(
		async (payload: ExceptionPayload) => {
			if (!accessToken || !idToken) {
				return { error: { id: 'UNAUTHORIZED', message: 'Not authenticated' }, status: 401 };
			}

			const res = await createException({ accessToken, idToken }, payload);

			if (!res.error) {
				await fetchData();
			}

			return res;
		},
		[accessToken, idToken, fetchData]
	);

	// delete exception — HTTP DELETE banished; no UI caller

	return {
		create,
		data,
		error,
		loading,
		pagination,
		refetch: fetchData,
	};
};

// Single Exception Hook

export interface UseExceptionParams {
	id: number;
	isEnabled?: boolean;
	query?: QueryParams;
}

export interface UseExceptionReturn {
	data: Exception | null;
	error: string | null;
	loading: boolean;
	refetch: () => Promise<void>;
}

export const useException = ({
	id,
	isEnabled = true,
	query = {},
}: UseExceptionParams): UseExceptionReturn => {
	// auth
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// state
	const [data, setData] = useState<Exception | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);

	// fetch data
	const queryKey = JSON.stringify(query);

	const fetchData = useCallback(async () => {
		if (!accessToken || !idToken || !id) {
			return;
		}

		setLoading(true);
		setError(null);

		try {
			const res = await fetchException({ accessToken, idToken }, id, query);

			if (res.error) {
				setError(res.error.message);
			} else {
				setData(res.data || null);
			}
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Failed to fetch exception');
		} finally {
			setLoading(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [accessToken, idToken, id, queryKey]);

	// initial fetch
	useEffect(() => {
		if (isEnabled && id) {
			fetchData();
		}
	}, [isEnabled, fetchData, id]);

	return {
		data,
		error,
		loading,
		refetch: fetchData,
	};
};
